import React, { useEffect, useRef, useState } from 'react';
import { Box, Text, Table, Thead, Tr, Th, Td, Tbody, Button, useDisclosure, Grid, GridItem, Spinner, Stack, HStack, Link as ChakraLink, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, VStack, FormControl, FormLabel, Input, Center, Circle, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react';
import { useNavigate, useParams, useLocation, Link as ReactRouterLink } from 'react-router-dom';
import Navbar from '../../elements/Navbar/Navbar';
import { generatePDFForce, generatePDF, pdfFinalChecker, generatePreview, handleRoles, isAuthenticated, getIdentity, getDocument, handleSignIn, postRequest, patchDocument, deleteDocument, checkSwitchableTemplate, confirmTemplateSwitch, documentValidator } from '../../ServiceRequest';
import DocumentStep from '../../elements/DocumentStep';
import moment from 'moment';
import { FaExclamation, FaQuestion, FaQuestionCircle } from 'react-icons/fa';
import Footer from '../../elements/Footer';
import ExtendedDocumentPreview from '../../elements/ExtendedDocumentPreview';



const Document: React.FC = () => {

    let user_name: string = '';
    const location = useLocation();
    const toast = useToast();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
    const { isOpen: isGeneratePDFOpen, onOpen: onGeneratePDFOpen, onClose: onGeneratePDFClose } = useDisclosure()
    const { isOpen: isTemplateSwitchOpen, onOpen: onTemplateSwitchOpen, onClose: onTemplateSwitchClose } = useDisclosure()

    if (!isAuthenticated()) {
        handleSignIn(location.pathname);
    }
    const navigate = useNavigate();
    const { project_id, document_id, template_name } = useParams<{
        project_id: string,
        document_id: string;
        template_name: string;
    }>();


    const [userRoles, setUserRoles] = useState<any>([]);
    const [document, setDocument] = useState<any>({});
    const [template, setTemplate] = useState<any>({});
    //const [templates, setTemplates] = useState<any[]>([]);

    const [documentValidatorResults, setDocumentValidatorResults] = useState<any[]>([])
    const [fieldsToBeAdded, setFieldsToBeAdded] = useState<any[] | null>(null);
    const [fieldsToBeRemoved, setFieldsToBeRemoved] = useState<any[] | null>(null);
    const [steps, setSteps] = useState<any[]>([]);
    const [selectedStep, setSelectedStep] = useState<any>(null);
    const [documentPreview, setDocumentPreview] = useState<any>(null);
    const [missingFields, setMissingFields] = useState<any[]>([]);
    const [optionalMissingFields, setOptionalMissingFields] = useState<any[]>([]);
    const [canGenerate, setCanGenerate] = useState<boolean>(false);

    const editDocumentFormRef = useRef<HTMLFormElement>(null);


    let stepsUserRoles = userRoles;

    const getCurrentDocument = async () => {
  const res = await getDocument(document_id!, project_id!, template_name!);

  if (res && res.success === true && res.action === "CONFIRM_CREATE" && res.payload) {
    const p = new URLSearchParams(res.payload);
    navigate(`/documents/confirm?${p.toString()}`, { replace: true });
    return;
  }

  if (!res || res.success !== true || !res.data) {
    toast({
      status: "error",
      title: "Unable to load document",
      description: "Please check the URL or try again.",
    });
    navigate("/", { replace: true });
    return;
  }

  setDocument(res); 
  setTemplate(res.data.template ?? {}); 

  handleCheckSwitchableTemplate(res.data._id);
};

    const editDocument = (step_number: number) => {
        navigate(`/${project_id}/${document_id}/${template_name}/${step_number}`);
    }

    /*     const getTemplatesList = async () => {
            const templates = await getRequest('/templates')
            setTemplates(templates);
        } */

    const generateDocumentPreviewFileUrl = async () => {
        setDocumentPreview(null)
        try {

            await generatePreview(document_id)
                .then(previewUrl => setDocumentPreview(previewUrl));

        } catch (err) {
            console.error(err);
        }
    }

    const getAllowedRoles = async (project_id: string, user_name: string) => {
        stepsUserRoles = await handleRoles(project_id, user_name);
        setUserRoles(stepsUserRoles);
    }

    if (isAuthenticated()) {
        const identity = getIdentity();
        user_name = identity!.username;
    }

    const handleGeneratePDF = async () => {
        const validator: any = await documentValidator(document_id);
        const invalid = validator?.data?.data?.body?.invalidFields ?? [];
        setDocumentValidatorResults(invalid);

        const verification: any = await pdfFinalChecker(document_id);
        setMissingFields(verification?.missingFields ?? []);
        setOptionalMissingFields(verification?.optionalMissingFields ?? []);
        setCanGenerate(!!verification?.check);

        onGeneratePDFOpen();
    };


    const copyUrlToClipboard = async () => {
        try {

            const url = window.location.href;
            await navigator.clipboard.writeText(url)

            toast({
                title: 'URL copied!',
                description: "The URL has been successfully copied to your clipboard.",
                status: 'success',
                position: 'top',
                duration: 5000,
                isClosable: true,
            })

        } catch (error) {

        }
    };


    const handleUpdateDocument = async () => {
        const formData = new FormData(editDocumentFormRef.current!)
        const payload: Record<string, any> = {};
        formData.forEach((value, key) => {
            payload[key] = value;
        })

        await patchDocument(document?.data?._id, payload)
            .then(() => { window.location.reload() })

    }

    const handleDeleteDocument = async () => {
        await deleteDocument(document?.data?._id)
            .then(() => { navigate('/') })
    }

    const handleCheckSwitchableTemplate = async (document_id: string) => {
        const result = await checkSwitchableTemplate(document_id);

        if (result.switchable) {
            onTemplateSwitchOpen()
            setFieldsToBeAdded(result.fieldsToBeAdded)
            setFieldsToBeRemoved(result.fieldsToBeRemoved)
        }
    }

    const handleConfirmTemplateSwitch = async () => {
        await confirmTemplateSwitch(document?.data?._id)
            .then(() => { window.location.reload() })

    }
    useEffect(() => {
        if (user_name && project_id) {
            getAllowedRoles(project_id, user_name);
        }
    }, [user_name])

    useEffect(() => {
        if (document_id) {
            getCurrentDocument();
            handleCheckSwitchableTemplate(document_id);
        }
    }, [document_id]);

    useEffect(() => {
        if (document?.data) {
            setSteps(Array.isArray(document?.data?.steps) ? document?.data?.steps : []);
            generateDocumentPreviewFileUrl();
        }
    }, [document]);

    if (!document?.success || !document?.data) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <Spinner size="xl" />
            </Box>
        );
    }


    return (
        <>
            <Navbar />

            <Box className="container" >
                <Grid templateColumns='repeat(12, 1fr)' gap={4}>
                    <GridItem colSpan={6}>

                        <div className="doc-toolbar">
                            <ChakraLink as={ReactRouterLink} to="/">Back to main section</ChakraLink>
                            <HStack spacing="30px">
                                <ChakraLink onClick={onEditOpen} className="doc-action-link">Edit</ChakraLink>
                                <ChakraLink onClick={copyUrlToClipboard} className="doc-action-link">Share</ChakraLink>
                                <ChakraLink onClick={onDeleteOpen} as={ReactRouterLink} className="doc-action-link link-red">Delete</ChakraLink>
                            </HStack>
                        </div>
                        <Text fontSize='sm'>Work Order #{document?.data?._id} (Project ID: <b>{document?.data?.projectId})</b></Text>
                        <Text fontSize='4xl' as='h1'>{document?.data?.name}</Text>
                        <Stack spacing={1} style={{ marginBottom: "30px" }}>
                            <Text fontSize='lg'>{document?.data?.description}</Text>
                            <HStack spacing="30px">
                                <Text fontSize='sm'><b>Created:</b> {moment(document?.data?.creationDate).format('MMMM Do YYYY, h:mm A')} <small>({moment(document?.data?.creationDate).local().fromNow()})</small></Text>
                                {document?.data?.lastModificationDate && (
                                    <Text fontSize='sm'><b>Updated:</b> {moment(document?.data?.lastModificationDate).format('MMMM Do YYYY, h:mm A')} <small>({moment(document?.data?.lastModificationDate).local().fromNow()})</small></Text>
                                )}

                            </HStack>
                        </Stack>
                        <Table variant="simple" size="sm">
                            <Thead>
                                <Tr>
                                    <Th>Step</Th>
                                    <Th>Status</Th>
                                    <Th></Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {steps.map((step: any, index: number) => {

                                    if (template.steps) {
                                        const currentStep = template.steps[index]

                                        const filled = step.stepHistory && step.stepHistory.length > 0;
                                        const isStepAllowed = (step: any): boolean => {
                                            var result = false;
                                            step.allowedRoles.map((role: any) => {
                                                result = stepsUserRoles.includes(role)
                                            })
                                            return result;
                                        }


                                        const allowed = isStepAllowed(currentStep);
                                        //const allowed = true;

                                        return (
                                            <DocumentStep
                                                key={index}
                                                step={step}
                                                allowed={allowed}
                                                currentStep={currentStep}
                                                selectedStep={selectedStep}
                                                filled={filled}
                                                editDocument={editDocument}
                                                setSelectedStep={setSelectedStep}
                                            />

                                        );
                                    }



                                })}
                                {steps.every((step: any) => step.stepHistory && step.stepHistory.length > 0) &&
                                    (
                                        <Tr>
                                            <Td>
                                                <Text fontSize="sm"><strong>Generate document</strong></Text>
                                            </Td>
                                            <Td>Click on the button to start checking the fields and generate the PDF</Td>
                                            <Td style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <Button className='el-btn' colorScheme="#333" style={{ cursor: "pointer" }} mb={5} onClick={() => handleGeneratePDF()}>Generate PDF</Button>

                                            </Td>
                                        </Tr>
                                    )
                                }

                            </Tbody>
                        </Table>

                    </GridItem>
                    <GridItem colSpan={6}>
                        <Text fontSize='2xl' as='h1'>Document preview</Text>

                        {documentPreview ? (<ExtendedDocumentPreview fileUrl={documentPreview} />) : (
                            <Box textAlign="center" mt={5}>
                                <Spinner size="xl" />
                                <Text fontSize="lg" color="gray.500" mt={4}>
                                    Loading document preview...
                                </Text>
                            </Box>
                        )}
                    </GridItem>
                </Grid>
            </Box>


            {/* Delete Modal */}
            <Modal isOpen={isDeleteOpen} size="xl" onClose={onDeleteClose} isCentered>
                <ModalOverlay bg='none' backdropFilter='auto' backdropBlur='10px' />
                <ModalContent className="modal" borderRadius="0px" boxShadow="xl" maxWidth="800px">
                    <ModalHeader fontSize="24px" fontWeight="bold">Delete document</ModalHeader>
                    <ModalCloseButton className="modal-close-button" />
                    <ModalBody className="modal-body">
                        <Center style={{ marginBottom: "30px" }}>
                            <Circle size='80px' bg='#962324' color='white'>
                                <FaExclamation style={{ fontSize: "2.5rem" }} />
                            </Circle>
                        </Center>
                        <Center>
                            <Text fontSize='md'>
                                Are you sure you want to delete this document? The action is not reversible.
                            </Text>
                        </Center>

                        <Center>
                            <HStack>
                                <Button
                                    className="el-btn"
                                    mt={5}
                                    onClick={onDeleteClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className="el-btn el-btn-danger"
                                    mt={5}
                                    onClick={handleDeleteDocument}
                                >
                                    Confirm
                                </Button>
                            </HStack>
                        </Center>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Edit Modal */}
            <Modal isOpen={isEditOpen} size="xl" onClose={onEditClose} isCentered>
                <ModalOverlay bg='none' backdropFilter='auto' backdropBlur='10px' />
                <ModalContent className="modal" borderRadius="0px" boxShadow="xl" maxWidth="800px">
                    <ModalHeader fontSize="24px" fontWeight="bold">Edit document properties</ModalHeader>
                    <ModalCloseButton className="modal-close-button" />
                    <ModalBody className="modal-body">
                        <Box as="form" ref={editDocumentFormRef}>
                            <VStack spacing={2} align="stretch">
                                <FormControl className="input-field" mb={4}>
                                    <FormLabel>Project ID</FormLabel>
                                    <Input
                                        isDisabled={true}
                                        defaultValue={document?.data?.projectId}
                                        className="dark-input"
                                    />
                                </FormControl>
                                <FormControl className="input-field" mb={4}>
                                    <FormLabel>Document name</FormLabel>
                                    <Input
                                        name="name"
                                        placeholder="Enter a name for this document"
                                        defaultValue={document?.data?.name}
                                        className="dark-input"
                                    />
                                </FormControl>
                                <FormControl className="input-field" mb={4}>
                                    <FormLabel>Document description</FormLabel>
                                    <Input
                                        name="description"
                                        placeholder="Enter a description"
                                        defaultValue={document?.data?.description}
                                        className="dark-input"
                                    />
                                </FormControl>
                                <Button
                                    className="el-btn"
                                    mt={5}
                                    onClick={handleUpdateDocument}
                                >
                                    Save
                                </Button>
                            </VStack>
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Generate PDF Modal */}
            <Modal isOpen={isGeneratePDFOpen} size="xl" onClose={onGeneratePDFClose} isCentered>
                <ModalOverlay bg='none' backdropFilter='auto' backdropBlur='10px' />
                <ModalContent className="modal" borderRadius="0px" boxShadow="xl" maxWidth="800px">
                    <ModalHeader fontSize="24px" fontWeight="bold">Generate PDF</ModalHeader>
                    <ModalCloseButton className="modal-close-button" />
                    <ModalBody className="modal-body">
                        <Box>

                            {(missingFields.length > 0 || optionalMissingFields.length > 0 || documentValidatorResults.length > 0) ? (
                                <div style={{ marginBottom: "20px", textAlign: "center" }}>
                                    <div>There are some missing or invalid fields in this document.</div>
                                    <div>You can fix them now or generate the PDF anyway.</div>


                                </div>
                            ) : (
                                <div>
                                    <Alert status='success' flexDirection='column'>
                                        <AlertIcon />
                                        <AlertTitle>Check completed</AlertTitle>
                                        <AlertDescription>
                                            All fields have been filled in correctly. Click on the button below to generate the PDF.
                                        </AlertDescription>
                                    </Alert>

                                </div>
                            )}
                            {documentValidatorResults.length > 0 && (

                                <div style={{ marginBottom: "20px" }}>
                                    <Alert status='error'>
                                        <AlertIcon />
                                        <AlertTitle>Invalid fields</AlertTitle>
                                    </Alert>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th style={{ width: "20%" }}>Invalid Field</Th>
                                                <Th style={{ width: "50%" }}>Reason</Th>
                                                <Th style={{ width: "20%" }}>Step</Th>
                                                <Th style={{ width: "10%" }}></Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {documentValidatorResults.map((field: any, index: number) => (

                                                <Tr key={index}>
                                                    <Td>{field.fieldLabel}</Td>
                                                    <Td>{field.message}</Td>
                                                    <Td>{field.fieldStep}</Td>
                                                    <Td>
                                                        <Button className="el-btn el-btn-sm" style={{ cursor: "pointer" }} onClick={() => editDocument(field.fieldStepNumber)}>
                                                            Go to step
                                                        </Button>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </div>
                            )}
                            {missingFields.length > 0 && (

                                <div style={{ marginBottom: "20px" }}>
                                    <Alert status='error'>
                                        <AlertIcon />
                                        <AlertTitle>Required fields</AlertTitle>
                                    </Alert>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th style={{ width: "50%" }}>Missing Field</Th>
                                                <Th style={{ width: "40%" }}>Step</Th>
                                                <Th style={{ width: "10%" }}></Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {missingFields.map((field: any, index: number) => (

                                                <Tr key={index}>
                                                    <Td>{field.field}</Td>
                                                    <Td>{field.stepName}</Td>
                                                    <Td>
                                                        <Button className="el-btn el-btn-sm" style={{ cursor: "pointer" }} onClick={() => editDocument(field.stepNumber)}>
                                                            Go to step
                                                        </Button>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </div>
                            )}

                            {optionalMissingFields.length > 0 && (
                                <div style={{ marginBottom: "20px" }}>
                                    <Alert status='warning'>
                                        <AlertIcon />
                                        <AlertTitle>Optional fields</AlertTitle>
                                    </Alert>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th style={{ width: "50%" }}>Missing Optional Field</Th>
                                                <Th style={{ width: "40%" }}>Step</Th>
                                                <Th style={{ width: "10%" }}></Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {optionalMissingFields.map((field: any, index: number) => (

                                                <Tr key={index}>
                                                    <Td>{field.field}</Td>
                                                    <Td>{field.stepName}</Td>
                                                    <Td>
                                                        <Button className="el-btn el-btn-sm" style={{ cursor: "pointer" }} onClick={() => editDocument(field.stepNumber)}>
                                                            Go to step
                                                        </Button>
                                                    </Td>
                                                </Tr>
                                            ))}

                                        </Tbody>
                                    </Table>
                                </div>

                            )}

                            {(canGenerate === true && documentValidatorResults.length === 0 && missingFields.length === 0) ? (
                                <Center>
                                    <Button className="el-btn" style={{ cursor: "pointer", marginTop: 20 }}
                                        onClick={() => generatePDF(document_id!)}>
                                        Generate PDF
                                    </Button>
                                </Center>
                            ) : (
                                <VStack spacing={4} mt={5}>
                                    <Alert status="warning" variant="subtle" flexDirection="column" alignItems="start">
                                        <div>
                                            <Box>
                                                <AlertDescription>
                                                    Are you sure you want to generate the PDF file with the above issues?
                                                </AlertDescription>
                                            </Box>
                                        </div>


                                        <Box style={{ width: "100%" }}>
                                            <HStack spacing="12px" className="action-btn-container">
                                                <Button className="el-btn el-btn-secondary" onClick={onGeneratePDFClose}>
                                                    Fix issues
                                                </Button>
                                                <Button
                                                    className="el-btn"
                                                    onClick={() => generatePDFForce(document_id!)}
                                                >
                                                    Generate PDF anyway
                                                </Button>
                                            </HStack>
                                        </Box>



                                    </Alert>



                                </VStack>
                            )}
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Template Switch Modal */}
            <Modal isOpen={isTemplateSwitchOpen} size="xl" onClose={onTemplateSwitchClose} isCentered>
                <ModalOverlay bg='none' backdropFilter='auto' backdropBlur='10px' />
                <ModalContent className="modal" borderRadius="0px" boxShadow="xl" maxWidth="800px">
                    <ModalHeader fontSize="24px" fontWeight="bold">Newer template available</ModalHeader>
                    <ModalCloseButton className="modal-close-button" />
                    <ModalBody className="modal-body">
                        <Center style={{ marginBottom: "30px" }}>
                            <Circle size='80px' bg='#000' color='white'>
                                <FaExclamation style={{ fontSize: "2.5rem" }} />
                            </Circle>
                        </Center>
                        <Center>
                            <Text fontSize='lg'>
                                A newer template is available for this document.
                            </Text>
                        </Center>
                        {(fieldsToBeAdded !== null && fieldsToBeRemoved !== null) && (
                            <Center>
                                <Text fontSize='md'>
                                    {(fieldsToBeAdded.length === 0 && fieldsToBeRemoved.length === 0) ? (
                                        <>
                                            Replacing this template with the newer one will not make any changes to the fields.
                                        </>
                                    ) : (
                                        <>
                                            Replacing this template with the most recent one will apply the following changes:
                                            {fieldsToBeAdded.length > 0 && (
                                                <div style={{ marginTop: "10px", backgroundColor: "#e0f8ef", padding: "5px" }}>
                                                    <strong> Fields that will be added:</strong>
                                                    {fieldsToBeAdded.map((field: any, index: number) => (
                                                        <div key={index}>
                                                            {field.label} on "{field.stepName}" section.
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {fieldsToBeRemoved.length > 0 && (
                                                <div style={{ marginTop: "10px", backgroundColor: "#f7e4e4", padding: "5px" }}>
                                                    <strong> Fields that will be removed:</strong>
                                                    {fieldsToBeRemoved.map((field: any, index: number) => (
                                                        <div key={index}>
                                                            {field.label} on "{field.stepName}" section.
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}

                                </Text>
                            </Center>
                        )}



                        <Center>
                            <HStack>
                                <Button
                                    className="el-btn el-btn-secondary"
                                    mt={5}
                                    onClick={onTemplateSwitchClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className="el-btn"
                                    mt={5}
                                    onClick={handleConfirmTemplateSwitch}
                                >
                                    Confirm
                                </Button>
                            </HStack>
                        </Center>
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Footer />
        </>

    );
};

export default Document;